import type { AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import {
  getHaulerCompanyId,
  isDevBypassAuth,
  isHaulerSession,
} from 'src/api/state';
// Dev-only mock layer with two independent branches:
//
// 1. Citizen dev-bypass (DEV_BYPASS_AUTH=true on the dev pill or .env): serves
//    a generic 3-property mock to skip the MitID gate on local dev.
//
// 2. Hauler session (HAULER_MODE=true after the demo EmailOTP login): serves
//    a static fixture (src/api/fixtures/mp-fredericia.json) for the property
//    endpoints. The fixture is generated by scripts/fetch-mp-fredericia-
//    containers.mjs, which pages through /api/v3/container/ with a user
//    PlatformAPIKey, filters to Marius Pedersen's containers (container_id
//    prefix "MP-"), and groups them by address into properties. We do this
//    client-side because (a) the CRM portal v1 API has no hauler concept, (b)
//    the v3 API exposes haulers/routes but has zero server-side filtering,
//    and (c) the anonymous tenant token the hauler session uses doesn't have
//    /api/v3/ access anyway. When backend ships either a hauler-scoped v1
//    endpoint or filterable v3 endpoints, delete the hauler branch here and
//    point the hauler portal at the real call.

type PropertyFixture = {
  id: string;
  location: {
    latitude: number;
    longitude: number;
    name: string;
  };
};

const MOCK_PROPERTIES: PropertyFixture[] = [
  {
    id: 'mock-prop-1',
    location: {
      latitude: 56.1629,
      longitude: 10.2039,
      name: 'Hovedgaden 1, 8000 Aarhus',
    },
  },
  {
    id: 'mock-prop-2',
    location: {
      latitude: 55.6761,
      longitude: 12.5683,
      name: 'Strandvejen 42, 2100 København',
    },
  },
  {
    id: 'mock-prop-3',
    location: {
      latitude: 55.4038,
      longitude: 10.4024,
      name: 'Kongevej 7, 5000 Odense',
    },
  },
];

// Per-hauler service constraints applied client-side. The fixture branch
// only ever returns MP-prefixed containers, but we keep the filter as a
// belt-and-braces defence in PropertyDetail / Properties so that if the
// fixture is ever stale or the data source switches, off-prefix containers
// still get hidden.
export type ServiceCapabilities = {
  containerIdPrefix?: string;
  fractions?: string[];
  containerTypes?: string[];
};

type HaulerCompany = {
  id: string;
  name: string;
  emailDomains: string[];
  tenants: string[]; // crm_hostnames this hauler services
  serviceCapabilities: ServiceCapabilities;
};

const HAULER_COMPANIES: Record<string, HaulerCompany> = {
  'marius-pedersen': {
    id: 'marius-pedersen',
    name: 'Marius Pedersen',
    emailDomains: ['mariuspedersen.dk'],
    tenants: ['fredericia-portal.wastehero.io'],
    // MP tags every container they service in the backoffice with an
    // "MP-{route_scheme}-{n}" naming convention (e.g. "MP-7002-0",
    // "MP-7004-160"). Prefix match against container.container_id is the
    // operational ground truth — eliminates fraction/type guessing and
    // never returns false positives.
    serviceCapabilities: {
      containerIdPrefix: 'MP-',
    },
  },
};

// Used by EmailOtpLogin to tag the session at OTP-success time. Returns the
// hauler-company id matching the email's domain, or null when no entry in
// the registry matches.
export const resolveHaulerCompanyFromEmail = (email: string): string | null => {
  const domain = email.split('@')[1]?.toLowerCase();
  if (!domain) return null;
  const match = Object.values(HAULER_COMPANIES).find((c) =>
    c.emailDomains.includes(domain)
  );
  return match?.id ?? null;
};

// Returns the service constraints for the currently-active hauler session,
// or null when no hauler is logged in (or the hauler is unrecognised). The
// hauler pages use this to filter the served data.
export const getCurrentHaulerCapabilities = (): ServiceCapabilities | null => {
  if (!isHaulerSession()) return null;
  const id = getHaulerCompanyId();
  const company = id ? HAULER_COMPANIES[id] : undefined;
  return company?.serviceCapabilities ?? null;
};

// Returns the tenants (crm_hostnames) the currently-active hauler is
// authorised to view. Used by the hauler pages to short-circuit fetches
// when the tenant in the URL isn't one this hauler services.
export const getCurrentHaulerTenants = (): string[] | null => {
  if (!isHaulerSession()) return null;
  const id = getHaulerCompanyId();
  const company = id ? HAULER_COMPANIES[id] : undefined;
  return company?.tenants ?? null;
};

// Mirrors Company.store.tsx's hostname resolution.
const getCurrentCrmHostname = (): string => {
  if (typeof window === 'undefined') return '';
  const fromStorage = localStorage.getItem('DEV_CRM_HOSTNAME');
  if (fromStorage) return fromStorage;
  return window.location.hostname.replace(/^www\./, '');
};

const makeTicketType = (
  id: string,
  name: string,
  serviceType: string,
  isContainerLevel = false
) => ({
  id,
  name,
  service: { id: `svc-${id}`, name: serviceType, service_type: serviceType },
  is_container_level: isContainerLevel,
  is_property_level: !isContainerLevel,
  portal_require_contact_information: false,
  portal_description: `Mock description for ${name}`,
  portal_date_label: 'Preferred date',
  portal_date_help_text: '',
  portal_date_required: false,
  portal_date_hidden: false,
  portal_note_label: 'Note',
  portal_note_help_text: '',
  portal_note_required: false,
  portal_note_hidden: false,
  portal_price_show_zero: true,
  portal_contact_information_name_required: false,
  portal_contact_information_phone_number_required: false,
  portal_contact_information_email_required: false,
  portal_contact_information_next_button_label: 'Next',
  portal_confirm_title: 'Confirm',
  portal_confirm_subtitle: '',
  portal_service_type_bulk_waste_limit: 10,
  portal_service_type_bulk_waste_limit_label: 'Items',
  portal_service_type_bulk_waste_limit_help_text: '',
  portal_dropdown_hidden: true,
  portal_dropdown_required: false,
  portal_dropdown_label: '',
  portal_dropdown_help_text: '',
  portal_dropdown_options: [],
});

const buildMockProperty = (id: string) => {
  const listEntry =
    MOCK_PROPERTIES.find((p) => p.id === id) ?? MOCK_PROPERTIES[0];
  return {
    id,
    type: { id: 'type-1', name: 'Residential' },
    location: listEntry.location,
    ticket_types: [
      makeTicketType('tt-1', 'Extra emptying', 'extra_emptying', true),
      makeTicketType('tt-2', 'Bulk waste pickup', 'bulk_waste_pickup', false),
      makeTicketType('tt-3', 'Missing collection', 'missing_collection', true),
    ],
  };
};

type MockResult = { status: number; data: unknown };
type Matcher = (config: InternalAxiosRequestConfig) => MockResult | null;

const citizenMatchers: Matcher[] = [
  // Property search list: GET property OR /property, optional ?search=...
  (config) => {
    if (config.method?.toUpperCase() !== 'GET') return null;
    const url = (config.url || '').replace(/^\/+/, '');
    if (!/^property\/?(\?|$)/.test(url)) return null;
    return { status: 200, data: MOCK_PROPERTIES };
  },
  // Property detail: GET property/:id (leaf, not property/:id/sub)
  (config) => {
    if (config.method?.toUpperCase() !== 'GET') return null;
    const url = (config.url || '').replace(/^\/+/, '');
    const match = /^property\/([^/?]+)\/?(\?|$)/.exec(url);
    if (!match) return null;
    return { status: 200, data: buildMockProperty(match[1]) };
  },
];

// Returns true when an active hauler session is operating on a tenant this
// hauler actually services. `isHaulerSession()` is already path-gated to
// /hauler URLs (see src/api/state.ts), so a leftover session flag never
// triggers the matchers from a citizen page.
const isMatchingHaulerTenant = (): boolean => {
  if (!isHaulerSession()) return false;
  const tenants = getCurrentHaulerTenants();
  if (!tenants) return false;
  return tenants.includes(getCurrentCrmHostname());
};

type FixtureContainer = {
  id: string;
  num_open_tickets: number;
  container: {
    id: string;
    container_id: string | null;
    waste_fraction: { id: string; name: string; color: string | null } | null;
    container_type: { id: string; name: string } | null;
  } | null;
};

type FixtureTicket = {
  id: string;
  ticket_id?: string;
  ticket_type?: string;
  description?: string | null;
  status: number;
  created_at: string;
  resolved_at?: string | null;
  preferred_date?: string | null;
  contact_name?: string | null;
  user?: { name?: string | null } | null;
  assignee?: { name?: string | null } | null;
  property?: { id: string; location?: { name?: string | null } | null } | null;
  container?: {
    container_id?: string | null;
    waste_fraction?: { name?: string | null } | null;
  } | null;
  attachments?: { url: string; mime_type?: string }[];
};

type FixtureProperty = {
  id: string;
  location: { name: string; latitude: number; longitude: number };
  containers: FixtureContainer[];
  tickets?: FixtureTicket[];
};

let fixtureCache: FixtureProperty[] | null = null;

// Loaded on demand so the 1.4MB fixture ships as its own chunk and is only
// fetched when a hauler session is active — never for citizens.
const loadFixtureProperties = async (): Promise<FixtureProperty[]> => {
  if (!fixtureCache) {
    const mod = await import('src/api/fixtures/mp-fredericia.json');
    fixtureCache = (mod.default as unknown as { properties: FixtureProperty[] })
      .properties;
  }
  return fixtureCache;
};

type HaulerMatcher = (
  config: InternalAxiosRequestConfig,
  fixtureProperties: FixtureProperty[]
) => MockResult | null;

const haulerMatchers: HaulerMatcher[] = [
  // Property list — return MP properties from the fixture, ignoring server-
  // side ?search (the hauler pages apply search client-side). Strip the
  // `containers` and `tickets` fields so the response matches the v1
  // property shape. Each property's `id` is the real backend id (resolved
  // by the fetch script), so the detail call goes straight to the real
  // backend.
  (config, fixtureProperties) => {
    if (!isMatchingHaulerTenant()) return null;
    if (config.method?.toUpperCase() !== 'GET') return null;
    const url = (config.url || '').replace(/^\/+/, '');
    if (!/^property\/?(\?|$)/.test(url)) return null;
    return {
      status: 200,
      data: fixtureProperties.map((p) => ({
        id: p.id,
        location: p.location,
      })),
    };
  },
  // Property containers — only intercept when the id matches a fixture
  // entry; otherwise pass through. Filtering happens here (not server-
  // side) so the hauler only sees MP-prefixed containers at the address,
  // not whatever other haulers also service there.
  (config, fixtureProperties) => {
    if (!isMatchingHaulerTenant()) return null;
    if (config.method?.toUpperCase() !== 'GET') return null;
    const url = (config.url || '').replace(/^\/+/, '');
    const match = /^property\/([^/?]+)\/property-containers\/?(\?|$)/.exec(url);
    if (!match) return null;
    const prop = fixtureProperties.find((p) => p.id === match[1]);
    if (!prop) return null;
    return { status: 200, data: prop.containers };
  },
  // Property tickets — serve fixture tickets (baked from /api/v3/ticket/ by
  // the fetch script) instead of hitting the real v1 endpoint, because the
  // v1 endpoint silently filters out Portal-source tickets and the hauler
  // demo needs both Platform + Portal in one view. Only intercept when the
  // id matches a fixture property; non-MP property requests fall through.
  (config, fixtureProperties) => {
    if (!isMatchingHaulerTenant()) return null;
    if (config.method?.toUpperCase() !== 'GET') return null;
    const url = (config.url || '').replace(/^\/+/, '');
    const match = /^property\/([^/?]+)\/tickets\/?(\?|$)/.exec(url);
    if (!match) return null;
    const prop = fixtureProperties.find((p) => p.id === match[1]);
    if (!prop) return null;
    return { status: 200, data: prop.tickets ?? [] };
  },
  // Single ticket: GET property/<id>/tickets/<ticketId>. Same reason as
  // above — serve from the fixture so Portal-source tickets are visible.
  (config, fixtureProperties) => {
    if (!isMatchingHaulerTenant()) return null;
    if (config.method?.toUpperCase() !== 'GET') return null;
    const url = (config.url || '').replace(/^\/+/, '');
    const match = /^property\/([^/?]+)\/tickets\/([^/?]+)\/?(\?|$)/.exec(url);
    if (!match) return null;
    const prop = fixtureProperties.find((p) => p.id === match[1]);
    if (!prop) return null;
    const ticket = (prop.tickets ?? []).find((t) => t.id === match[2]);
    if (!ticket) return null;
    return { status: 200, data: ticket };
  },
  // /property/<id> (detail) is NOT intercepted — the real backend serves
  // it. The list above uses real ids, so the citizen-anonymous tenant
  // token reaches it correctly.
];

const applyMock = (
  config: InternalAxiosRequestConfig,
  hit: MockResult,
  source: 'citizen' | 'hauler'
) => {
  console.info(
    `[devMock:${source}] intercepted ${config.method?.toUpperCase()} ${config.url}`
  );

  config.adapter = async () => ({
    data: hit.data,
    status: hit.status,
    statusText: 'OK (dev mock)',
    headers: {},
    config,
  });
};

export const installDevMocks = (...instances: AxiosInstance[]) => {
  // Per-request adapter override via interceptor — leaves axios's default
  // adapter resolution intact for everything else. Install on every instance
  // the app uses since axios interceptors don't propagate across instances.
  // Both branches re-evaluate per request so toggling the dev bypass or
  // logging in/out of a hauler session takes effect immediately.
  instances.forEach((instance) => {
    instance.interceptors.request.use(async (config) => {
      if (isDevBypassAuth()) {
        const hit = citizenMatchers
          .map((tryMatch) => tryMatch(config))
          .find(Boolean);
        if (hit) {
          applyMock(config, hit, 'citizen');
          return config;
        }
      }
      if (isHaulerSession()) {
        const props = await loadFixtureProperties();
        const hit = haulerMatchers
          .map((tryMatch) => tryMatch(config, props))
          .find(Boolean);
        if (hit) {
          applyMock(config, hit, 'hauler');
          return config;
        }
      }
      return config;
    });
  });
};
